import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  motion,
  AnimatePresence,
  useDragControls,
  useMotionValue,
  animate,
} from "framer-motion";
import {
  Clock,
  Square,
  ChevronDown,
  ChevronUp,
  GripVertical,
  Loader2,
  Check,
  Pencil,
  X,
  FileText,
  DollarSign,
  Timer,
  AlertTriangle,
  Briefcase,
  MonitorPlay,
  MonitorOff,
} from "lucide-react";
import { useRunningTimer } from "../../contexts/RunningTimerContext";
import { invoicesApi, taxSettingsApi, mattersApi, screenCaptureApi } from "../../services/api";
import { useUserDataContext } from "../../context/UserDataContext";
import {
  TaxConfig,
  DEFAULT_TAX_CONFIG,
  calculateTaxCents,
} from "../../utils/taxCalculation";
import SearchableSelect, { SelectOption } from "./SearchableSelect";
import QuickCreateMatterModal from "./QuickCreateMatterModal";
import ConfirmDialog from "./ConfirmDialog";
import { notifyError, notifySuccess } from "./Toast";

/** YYYY-MM-DD in the user's local time zone (not UTC). */
const toLocalDateString = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Format seconds to HH:MM:SS */
const formatElapsed = (totalSec: number) => {
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

const formatCurrency = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-CA", { minimumFractionDigits: 2 })}`;

/** Hook to detect mobile viewport (< 768px) */
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
};

const FloatingTimer: React.FC = () => {
  const {
    runningTimer,
    stoppedEntry,
    elapsedSeconds,
    stopTimer,
    updateRunningTimer,
    updateStoppedEntry,
    dismissStoppedEntry,
    loading,
  } = useRunningTimer();

  const { firmData } = useUserDataContext();
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(false);
  const [stopping, setStopping] = useState(false);

  // Screen capture state
  const isElite = firmData?.subscriptionStatus === "elite" || firmData?.isOnTrial;
  const [screenCaptureActive, setScreenCaptureActive] = useState(false);
  const [captureSessionId, setCaptureSessionId] = useState<string | null>(null);
  const captureStreamRef = useRef<MediaStream | null>(null);
  const captureIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const previousHashRef = useRef<string | undefined>(undefined);
  const [captureCount, setCaptureCount] = useState(0);

  const startScreenCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { width: 1280, height: 720 },
        audio: false,
      });
      const { data } = await screenCaptureApi.start();
      const sessionId = data.sessionId || data.data?.sessionId;
      setCaptureSessionId(sessionId);
      captureStreamRef.current = stream;
      setScreenCaptureActive(true);
      setCaptureCount(0);
      previousHashRef.current = undefined;

      const video = document.createElement("video");
      video.srcObject = stream;
      await video.play();
      const canvas = document.createElement("canvas");
      canvas.width = 1280;
      canvas.height = 720;
      const ctx = canvas.getContext("2d")!;

      captureIntervalRef.current = setInterval(async () => {
        ctx.drawImage(video, 0, 0, 1280, 720);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.6);
        const imageBase64 = dataUrl.split(",")[1];
        try {
          const res = await screenCaptureApi.frame({ sessionId, imageBase64, previousHash: previousHashRef.current });
          previousHashRef.current = res.data.hash || res.data.data?.hash;
          if (!res.data.skipped && !res.data.data?.skipped) setCaptureCount((c) => c + 1);
        } catch {}
      }, 60000);

      stream.getVideoTracks()[0].onended = () => stopScreenCapture();
    } catch {
      // Permission denied or not supported
    }
  };

  const stopScreenCapture = async () => {
    if (captureIntervalRef.current) clearInterval(captureIntervalRef.current);
    captureIntervalRef.current = null;
    if (captureStreamRef.current) {
      captureStreamRef.current.getTracks().forEach((t) => t.stop());
      captureStreamRef.current = null;
    }
    if (captureSessionId) {
      try { await screenCaptureApi.stop(captureSessionId); } catch {}
    }
    setScreenCaptureActive(false);
    setCaptureSessionId(null);
    setCaptureCount(0);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (captureIntervalRef.current) clearInterval(captureIntervalRef.current);
      if (captureStreamRef.current) captureStreamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);
  const dragControls = useDragControls();
  const containerRef = useRef<HTMLDivElement>(null);

  // ── Matter selector state (for timers without a matter) ───────
  const [matterOptions, setMatterOptions] = useState<SelectOption[]>([]);
  const [mattersLoading, setMattersLoading] = useState(false);
  const [showQuickCreateMatter, setShowQuickCreateMatter] = useState(false);
  const mattersFetched = useRef(false);

  // Drag position tracked via motion values so we can animate them back
  const dragX = useMotionValue(0);
  const dragY = useMotionValue(0);

  // ── Stopped entry review state ──────────────────────────────────
  const [editMinutes, setEditMinutes] = useState(0);
  const [editNarrative, setEditNarrative] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // ── Invoice generation state ────────────────────────────────────
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [invoiceSent, setInvoiceSent] = useState(false);
  // "sent" = created and emailed; "created" = created but the email failed
  const [invoiceResult, setInvoiceResult] = useState<"sent" | "created" | null>(null);
  const [confirmInvoiceOpen, setConfirmInvoiceOpen] = useState(false);
  const [taxConfig, setTaxConfig] = useState<TaxConfig>(DEFAULT_TAX_CONFIG);

  // ── Mobile bottom sheet state ───────────────────────────────────
  const [mobileSheetExpanded, setMobileSheetExpanded] = useState(false);

  // Load tax config
  useEffect(() => {
    taxSettingsApi
      .get()
      .then((res) => {
        const d = res.data?.data || res.data;
        if (d?.taxMode) {
          setTaxConfig({
            taxMode: d.taxMode,
            hstRate: d.hstRate ?? 13,
            gstRate: d.gstRate ?? 5,
            pstRate: d.pstRate ?? 0,
          });
        }
      })
      .catch(() => {});
  }, []);

  // Determine if the running timer has no matter assigned
  const hasMatter = runningTimer
    ? !!(
        runningTimer.matterId &&
        (typeof runningTimer.matterId === "object"
          ? runningTimer.matterId._id
          : runningTimer.matterId)
      )
    : true;

  // Fetch matters list when a timer is running without a matter
  useEffect(() => {
    if (!runningTimer?.timer?.isRunning || hasMatter || mattersFetched.current) return;
    setMattersLoading(true);
    mattersApi
      .list({ limit: 200, sort: "-createdAt" })
      .then((res) => {
        const items = res.data?.data || res.data || [];
        setMatterOptions(
          items.map((m: any) => ({
            value: m._id,
            label: m.matterNumber ? `${m.matterNumber} — ${m.title}` : m.title,
            description: m.clientId?.displayName || undefined,
          }))
        );
        mattersFetched.current = true;
      })
      .catch(() => {})
      .finally(() => setMattersLoading(false));
  }, [runningTimer, hasMatter]);

  // Reset fetch flag when timer stops
  useEffect(() => {
    if (!runningTimer) mattersFetched.current = false;
  }, [runningTimer]);

  const handleAssignMatter = async (matterId: string | null) => {
    if (!matterId || !runningTimer) return;
    // Find the selected matter to get its clientId
    const matter = matterOptions.find((m) => m.value === matterId);
    await updateRunningTimer({ matterId });
  };

  const handleMatterCreated = async (matter: { _id: string; title: string; matterNumber: string }) => {
    setShowQuickCreateMatter(false);
    // Add to options and assign
    setMatterOptions((prev) => [
      { value: matter._id, label: `${matter.matterNumber} — ${matter.title}` },
      ...prev,
    ]);
    await updateRunningTimer({ matterId: matter._id });
  };

  // Sync edit fields when stoppedEntry arrives + snap back into view
  useEffect(() => {
    if (stoppedEntry) {
      setEditMinutes(stoppedEntry.minutes || 1);
      setEditNarrative(stoppedEntry.narrative || "");
      setSaved(false);
      setInvoiceSent(false);
      setInvoiceResult(null);
      setConfirmInvoiceOpen(false);
      setCollapsed(false);
      setMobileSheetExpanded(true);

      // Smoothly animate back to default position so the review card isn't cut off
      animate(dragX, 0, { type: "spring", stiffness: 400, damping: 35 });
      animate(dragY, 0, { type: "spring", stiffness: 400, damping: 35 });
    }
  }, [stoppedEntry, dragX, dragY]);

  const handleStop = async () => {
    setStopping(true);
    try {
      await stopTimer();
    } catch (err) {
      notifyError("Couldn't stop the timer.", err);
    } finally {
      setStopping(false);
    }
  };

  const handleSaveEdits = async () => {
    if (!stoppedEntry) return;
    setSaving(true);
    try {
      await updateStoppedEntry(stoppedEntry._id, {
        minutes: editMinutes,
        narrative: editNarrative.trim(),
      });
      setSaved(true);
      setTimeout(() => {
        dismissStoppedEntry();
        setMobileSheetExpanded(false);
      }, 1500);
    } catch (err) {
      notifyError("Couldn't save the time entry.", err);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateInvoice = async () => {
    if (!stoppedEntry) return;
    setConfirmInvoiceOpen(false);
    setGeneratingInvoice(true);
    try {
      await updateStoppedEntry(stoppedEntry._id, {
        minutes: editMinutes,
        narrative: editNarrative.trim(),
      });

      const amountCents = Math.round(
        (editMinutes / 60) * (stoppedEntry.rateCents || 0)
      );
      const { totalTaxCents } = stoppedEntry.billable
        ? calculateTaxCents(amountCents, taxConfig)
        : { totalTaxCents: 0 };

      const entryMatterId =
        stoppedEntry.matterId && typeof stoppedEntry.matterId === "object"
          ? stoppedEntry.matterId._id
          : stoppedEntry.matterId;
      const entryClientId =
        stoppedEntry.clientId && typeof stoppedEntry.clientId === "object"
          ? stoppedEntry.clientId._id
          : stoppedEntry.clientId;

      const res = await invoicesApi.create({
        matterId: entryMatterId,
        clientId: entryClientId,
        lineItems: [
          {
            kind: "time",
            description: editNarrative.trim() || "Legal services",
            quantity: parseFloat((editMinutes / 60).toFixed(2)),
            unitPriceCents: stoppedEntry.rateCents || 0,
            amountCents,
            taxable: stoppedEntry.billable,
            taxAmountCents: totalTaxCents,
            // Links the entry so it is marked billed and not invoiced twice
            timeEntryIds: [stoppedEntry._id],
          },
        ],
        dueAt: toLocalDateString(new Date(Date.now() + 30 * 86400000)),
      });

      const invoiceId = res.data?._id;
      const invoiceNumber = res.data?.invoiceNumber;
      let sent = false;
      if (invoiceId) {
        try {
          await invoicesApi.send(invoiceId);
          sent = true;
        } catch (sendErr) {
          notifyError(
            `${invoiceNumber ? `Invoice ${invoiceNumber}` : "The invoice"} was created as a draft but couldn't be emailed. Send it from Billing.`,
            sendErr
          );
        }
      }

      setInvoiceSent(true);
      setInvoiceResult(sent ? "sent" : "created");
      if (sent) {
        notifySuccess(invoiceNumber ? `Invoice ${invoiceNumber} sent.` : "Invoice sent.");
      }
      setTimeout(() => {
        dismissStoppedEntry();
        setMobileSheetExpanded(false);
      }, sent ? 2000 : 3500);
    } catch (err) {
      notifyError("Couldn't create the invoice.", err);
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const handleMobileDismiss = useCallback(() => {
    setMobileSheetExpanded(false);
    setTimeout(() => dismissStoppedEntry(), 300);
  }, [dismissStoppedEntry]);

  // ── Running timer display info ──────────────────────────────────
  const matterTitle =
    runningTimer && runningTimer.matterId && typeof runningTimer.matterId === "object"
      ? runningTimer.matterId.title
      : null;
  const matterNumber =
    runningTimer && runningTimer.matterId && typeof runningTimer.matterId === "object"
      ? runningTimer.matterId.matterNumber
      : undefined;
  const narrative = runningTimer?.narrative || "Timer in progress";

  // ── Stopped entry display info ──────────────────────────────────
  const stoppedMatterTitle =
    stoppedEntry && stoppedEntry.matterId && typeof stoppedEntry.matterId === "object"
      ? stoppedEntry.matterId.title
      : null;
  const stoppedMatterNumber =
    stoppedEntry && stoppedEntry.matterId && typeof stoppedEntry.matterId === "object"
      ? stoppedEntry.matterId.matterNumber
      : undefined;

  const stoppedAmountCents = stoppedEntry
    ? Math.round((editMinutes / 60) * (stoppedEntry.rateCents || 0))
    : 0;
  const stoppedClientName =
    stoppedEntry && stoppedEntry.clientId && typeof stoppedEntry.clientId === "object"
      ? stoppedEntry.clientId.displayName || null
      : null;

  const isRunning = runningTimer?.timer?.isRunning;
  const isStopped = !!stoppedEntry && !isRunning;
  const isVisible = isRunning || !!stoppedEntry;

  // ════════════════════════════════════════════════════════════════
  //  MOBILE LAYOUT
  // ════════════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <>
      <AnimatePresence>
        {isVisible && (
          <>
            {/* ── MOBILE RUNNING: Bottom mini-bar ──────────────── */}
            {isRunning && (
              <motion.div
                key="mobile-running-bar"
                initial={{ y: 80, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 80, opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="fixed bottom-0 left-0 right-0 z-[80]"
              >
                {/* Safe area padding for notch phones */}
                <div className="bg-gradient-to-r from-indigo-950 to-indigo-900 border-t border-indigo-800/50">
                  <div className="flex items-center gap-3 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
                    {/* Pulsing dot */}
                    <span className="relative flex h-2.5 w-2.5 shrink-0">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
                    </span>

                    {/* Timer */}
                    <span className="text-cyan-300 font-mono text-base font-bold tracking-wider shrink-0">
                      {formatElapsed(elapsedSeconds)}
                    </span>

                    {/* Matter info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-indigo-100 text-sm font-medium truncate">
                        {narrative}
                      </p>
                      {hasMatter ? (
                        <p className="text-indigo-400/70 text-[11px] truncate">
                          {matterNumber
                            ? `${matterNumber} — ${matterTitle}`
                            : matterTitle}
                        </p>
                      ) : (
                        <p className="text-amber-400/80 text-[11px] flex items-center gap-1">
                          <AlertTriangle className="h-2.5 w-2.5" />
                          No matter assigned
                        </p>
                      )}
                    </div>

                    {/* Stop button */}
                    <button
                      onClick={handleStop}
                      disabled={stopping || loading}
                      className="bg-red-500/20 hover:bg-red-500/30 active:bg-red-500/40 disabled:opacity-50 text-red-300 rounded-xl px-4 py-2 flex items-center gap-2 text-sm font-semibold transition-colors shrink-0"
                    >
                      {stopping ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Square className="h-3 w-3 fill-current" />
                      )}
                      Stop
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── MOBILE STOPPED: Bottom sheet ─────────────────── */}
            {isStopped && (
              <>
                {/* Backdrop */}
                <motion.div
                  key="mobile-sheet-backdrop"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: mobileSheetExpanded ? 1 : 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="fixed inset-0 bg-black/30 z-[79]"
                  onClick={handleMobileDismiss}
                  style={{
                    pointerEvents: mobileSheetExpanded ? "auto" : "none",
                  }}
                />

                <motion.div
                  key="mobile-stopped-sheet"
                  initial={{ y: "100%" }}
                  animate={{ y: mobileSheetExpanded ? 0 : "calc(100% - 64px)" }}
                  exit={{ y: "100%" }}
                  transition={{
                    type: "spring",
                    stiffness: 350,
                    damping: 35,
                  }}
                  className="fixed bottom-0 left-0 right-0 z-[80] bg-white rounded-t-2xl shadow-[0_-8px_30px_rgba(0,0,0,0.12)]"
                  style={{
                    maxHeight: "85vh",
                    paddingBottom: "env(safe-area-inset-bottom)",
                  }}
                >
                  {/* Accent bar + drag handle */}
                  <div className="h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-t-2xl" />
                  <div
                    className="flex items-center justify-center pt-2 pb-1 cursor-grab"
                    onClick={() => setMobileSheetExpanded((v) => !v)}
                  >
                    <div className="w-10 h-1 rounded-full bg-slate-300" />
                  </div>

                  {/* Collapsed peek bar — always visible */}
                  <div
                    className="flex items-center gap-3 px-4 py-2"
                    onClick={() =>
                      !mobileSheetExpanded && setMobileSheetExpanded(true)
                    }
                  >
                    <div className="h-8 w-8 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                      <Check className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800">
                        Time Recorded
                      </p>
                      <p className="text-[11px] text-slate-400 truncate">
                        {stoppedMatterTitle
                          ? stoppedMatterNumber
                            ? `${stoppedMatterNumber} — ${stoppedMatterTitle}`
                            : stoppedMatterTitle
                          : "No matter assigned"}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-sm font-bold font-mono text-slate-700">
                        {editMinutes}m
                      </span>
                      <span className="text-sm font-bold text-emerald-600">
                        {formatCurrency(stoppedAmountCents)}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMobileDismiss();
                        }}
                        className="text-slate-300 hover:text-slate-500 p-1"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Expanded form */}
                  <AnimatePresence>
                    {mobileSheetExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        {/* Summary row */}
                        <div className="px-4 py-3 bg-slate-50/80 border-t border-slate-100">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <Clock className="h-3.5 w-3.5 text-slate-400" />
                              <span className="text-xl font-bold font-mono text-slate-800">
                                {editMinutes}
                                <span className="text-sm font-medium text-slate-400 ml-0.5">
                                  min
                                </span>
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <DollarSign className="h-3.5 w-3.5 text-emerald-500" />
                              <span className="text-xl font-bold text-emerald-600">
                                {formatCurrency(stoppedAmountCents)}
                              </span>
                            </div>
                          </div>
                          {stoppedEntry.rateCents > 0 && (
                            <p className="text-[11px] text-slate-400 mt-1">
                              ${(stoppedEntry.rateCents / 100).toFixed(2)}/hr
                              {stoppedEntry.billable
                                ? " · Billable"
                                : " · Non-billable"}
                            </p>
                          )}
                        </div>

                        {/* Edit form */}
                        <div className="px-4 py-4 space-y-3">
                          <div>
                            <label className="block text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1.5">
                              Description
                            </label>
                            <input
                              type="text"
                              value={editNarrative}
                              onChange={(e) => setEditNarrative(e.target.value)}
                              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-shadow"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1.5">
                              Time (minutes)
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={editMinutes}
                              onChange={(e) =>
                                setEditMinutes(
                                  Math.max(1, parseInt(e.target.value) || 1)
                                )
                              }
                              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-shadow"
                            />
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="px-4 pb-4 space-y-2.5">
                          <div className="flex gap-2.5">
                            <button
                              onClick={handleMobileDismiss}
                              className="flex-1 px-3 py-3 rounded-xl border border-slate-200 text-sm font-medium text-slate-500 hover:bg-slate-50 active:bg-slate-100 transition-colors"
                            >
                              Dismiss
                            </button>
                            <button
                              onClick={handleSaveEdits}
                              disabled={saving || saved}
                              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-3 rounded-xl text-white text-sm font-bold disabled:opacity-60 transition-all"
                              style={{
                                backgroundColor: saved
                                  ? "#10b981"
                                  : "var(--color-primary)",
                              }}
                            >
                              {saving ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : saved ? (
                                <Check className="h-4 w-4" />
                              ) : (
                                <Pencil className="h-4 w-4" />
                              )}
                              {saving
                                ? "Saving..."
                                : saved
                                ? "Saved!"
                                : "Save Entry"}
                            </button>
                          </div>

                          {stoppedEntry.billable && (
                            <button
                              onClick={() => setConfirmInvoiceOpen(true)}
                              disabled={generatingInvoice || invoiceSent}
                              className={`w-full flex items-center justify-center gap-2 px-3 py-3 rounded-xl text-sm font-bold transition-all ${
                                invoiceSent
                                  ? invoiceResult === "created"
                                    ? "bg-amber-500 text-white"
                                    : "bg-emerald-500 text-white"
                                  : "bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100 active:bg-slate-200"
                              } disabled:opacity-60`}
                            >
                              {generatingInvoice ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : invoiceSent ? (
                                <Check className="h-4 w-4" />
                              ) : (
                                <FileText className="h-4 w-4" />
                              )}
                              {generatingInvoice
                                ? "Creating Invoice..."
                                : invoiceSent
                                ? invoiceResult === "created"
                                ? "Invoice Created (not sent)"
                                : "Invoice Sent!"
                                : `Invoice for ${formatCurrency(stoppedAmountCents)}`}
                            </button>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </>
            )}
          </>
        )}
      </AnimatePresence>

      {/* Quick Create Matter Modal (mobile) */}
      <QuickCreateMatterModal
        open={showQuickCreateMatter}
        onClose={() => setShowQuickCreateMatter(false)}
        onCreated={handleMatterCreated}
      />
    </>
    );
  }

  // ════════════════════════════════════════════════════════════════
  //  DESKTOP LAYOUT (existing draggable widget)
  // ════════════════════════════════════════════════════════════════
  return (
    <>
    <AnimatePresence>
      {isVisible && (
        <motion.div
          ref={containerRef}
          key="floating-timer"
          initial={{ opacity: 0, y: 60, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 60, scale: 0.9 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          drag
          dragControls={dragControls}
          dragMomentum={false}
          dragElastic={0.1}
          className="fixed bottom-6 left-6 z-[80] select-none"
          style={{ x: dragX, y: dragY, touchAction: "none" }}
        >
          {/* ── RUNNING STATE ─────────────────────────────────── */}
          {isRunning && (
            <div className="rounded-2xl shadow-2xl overflow-hidden min-w-[280px] max-w-[320px] border border-indigo-200/50">
              {/* Header — dark indigo gradient */}
              <div
                className="flex items-center gap-2 px-3 py-2.5 cursor-grab active:cursor-grabbing bg-gradient-to-r from-indigo-950 to-indigo-900"
                onPointerDown={(e) => dragControls.start(e)}
              >
                <GripVertical className="h-3.5 w-3.5 text-white/40 shrink-0" />
                <div className="h-6 w-6 rounded-md bg-cyan-500/20 flex items-center justify-center shrink-0">
                  <Timer className="h-3.5 w-3.5 text-cyan-400" />
                </div>
                <div className="flex-1 flex items-center gap-2">
                  <span className="text-cyan-300 font-mono text-sm font-bold tracking-wider">
                    {formatElapsed(elapsedSeconds)}
                  </span>
                  {/* Pulsing dot */}
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                  </span>
                </div>
                <button
                  onClick={() => setCollapsed((c) => !c)}
                  className="text-white/50 hover:text-white/80 transition-colors p-0.5"
                >
                  {collapsed ? (
                    <ChevronUp className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5" />
                  )}
                </button>
                <button
                  onClick={handleStop}
                  disabled={stopping || loading}
                  className="bg-red-500/20 hover:bg-red-500/30 disabled:opacity-50 text-red-300 rounded-lg px-2.5 py-1 flex items-center gap-1.5 text-xs font-medium transition-colors"
                >
                  {stopping ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Square className="h-2.5 w-2.5 fill-current" />
                  )}
                  Stop
                </button>
              </div>

              {/* Details */}
              <AnimatePresence>
                {!collapsed && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="overflow-hidden bg-indigo-950/95"
                  >
                    <div className="px-3 py-2.5 space-y-1.5">
                      {hasMatter ? (
                        <p className="text-[11px] text-indigo-300/70 truncate">
                          {matterNumber
                            ? `${matterNumber} — ${matterTitle}`
                            : matterTitle}
                        </p>
                      ) : (
                        <>
                          <div className="flex items-center gap-1.5 mb-1">
                            <AlertTriangle className="h-3 w-3 text-amber-400" />
                            <span className="text-[11px] text-amber-300 font-medium">
                              No matter assigned
                            </span>
                          </div>
                          <SearchableSelect
                            options={matterOptions}
                            value={null}
                            onChange={handleAssignMatter}
                            placeholder="Select matter..."
                            searchPlaceholder="Search matters..."
                            icon={Briefcase}
                            loading={mattersLoading}
                            addNew={{
                              label: "Create New Matter",
                              onAdd: () => setShowQuickCreateMatter(true),
                            }}
                            className="[&_button]:!bg-indigo-900/60 [&_button]:!ring-indigo-700 [&_button]:!text-indigo-100 [&_button]:hover:!ring-indigo-500 [&_button_.text-slate-400]:!text-indigo-400"
                          />
                        </>
                      )}
                      <p className="text-sm font-medium text-indigo-100 truncate">
                        {narrative}
                      </p>
                      {runningTimer.rateCents > 0 && (
                        <p className="text-[11px] text-indigo-400/60">
                          ${(runningTimer.rateCents / 100).toFixed(2)}/hr
                          {runningTimer.billable ? "" : " · Non-billable"}
                        </p>
                      )}

                      {/* Auto-Capture toggle */}
                      {isElite && (
                        <button
                          onClick={screenCaptureActive ? stopScreenCapture : startScreenCapture}
                          className={`mt-1.5 w-full flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${
                            screenCaptureActive
                              ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30"
                              : "bg-indigo-800/50 text-indigo-300 hover:bg-indigo-700/50"
                          }`}
                        >
                          {screenCaptureActive ? (
                            <>
                              <MonitorOff className="h-3 w-3" />
                              Auto-Capture On · {captureCount} frames
                            </>
                          ) : (
                            <>
                              <MonitorPlay className="h-3 w-3" />
                              Enable Auto-Capture
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* ── STOPPED / REVIEW STATE ──────────────────────── */}
          {isStopped && (
            <div className="rounded-2xl shadow-2xl overflow-hidden min-w-[300px] max-w-[340px] bg-white border border-slate-200">
              {/* Accent bar */}
              <div className="h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

              {/* Header */}
              <div
                className="flex items-center gap-2.5 px-4 py-3 cursor-grab active:cursor-grabbing border-b border-slate-100"
                onPointerDown={(e) => dragControls.start(e)}
              >
                <GripVertical className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                <div className="h-7 w-7 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                  <Check className="h-3.5 w-3.5 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800">
                    Time Recorded
                  </p>
                  <p className="text-[11px] text-slate-400 truncate">
                    {stoppedMatterNumber
                      ? `${stoppedMatterNumber} — ${stoppedMatterTitle}`
                      : stoppedMatterTitle}
                  </p>
                </div>
                <button
                  onClick={dismissStoppedEntry}
                  className="text-slate-300 hover:text-slate-500 transition-colors p-0.5"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Summary banner */}
              <div className="px-4 py-3 bg-slate-50/80">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5 text-slate-400" />
                      <span className="text-lg font-bold font-mono text-slate-800">
                        {editMinutes}
                        <span className="text-sm font-medium text-slate-400 ml-0.5">
                          min
                        </span>
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <DollarSign className="h-3.5 w-3.5 text-emerald-500" />
                    <span className="text-lg font-bold text-emerald-600">
                      {formatCurrency(stoppedAmountCents)}
                    </span>
                  </div>
                </div>
                {stoppedEntry.rateCents > 0 && (
                  <p className="text-[11px] text-slate-400 mt-1">
                    ${(stoppedEntry.rateCents / 100).toFixed(2)}/hr
                    {stoppedEntry.billable
                      ? " · Billable"
                      : " · Non-billable"}
                  </p>
                )}
              </div>

              {/* Edit form */}
              <div className="px-4 py-3 space-y-2.5">
                {/* Description */}
                <div>
                  <label className="block text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    value={editNarrative}
                    onChange={(e) => setEditNarrative(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-shadow"
                  />
                </div>

                {/* Minutes */}
                <div>
                  <label className="block text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1">
                    Time (minutes)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={editMinutes}
                    onChange={(e) =>
                      setEditMinutes(
                        Math.max(1, parseInt(e.target.value) || 1)
                      )
                    }
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-shadow"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="px-4 pb-4 space-y-2">
                <div className="flex gap-2">
                  <button
                    onClick={dismissStoppedEntry}
                    className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-xs font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
                  >
                    Dismiss
                  </button>
                  <button
                    onClick={handleSaveEdits}
                    disabled={saving || saved}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-white text-xs font-bold disabled:opacity-60 transition-all"
                    style={{
                      backgroundColor: saved
                        ? "#10b981"
                        : "var(--color-primary)",
                    }}
                  >
                    {saving ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : saved ? (
                      <Check className="h-3 w-3" />
                    ) : (
                      <Pencil className="h-3 w-3" />
                    )}
                    {saving ? "Saving..." : saved ? "Saved!" : "Save Entry"}
                  </button>
                </div>

                {/* Generate Invoice */}
                {stoppedEntry.billable && (
                  <button
                    onClick={() => setConfirmInvoiceOpen(true)}
                    disabled={generatingInvoice || invoiceSent}
                    className={`w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${
                      invoiceSent
                        ? invoiceResult === "created"
                          ? "bg-amber-500 text-white"
                          : "bg-emerald-500 text-white"
                        : "bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-800"
                    } disabled:opacity-60`}
                  >
                    {generatingInvoice ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : invoiceSent ? (
                      <Check className="h-3 w-3" />
                    ) : (
                      <FileText className="h-3 w-3" />
                    )}
                    {generatingInvoice
                      ? "Creating Invoice..."
                      : invoiceSent
                      ? invoiceResult === "created"
                                ? "Invoice Created (not sent)"
                                : "Invoice Sent!"
                      : `Invoice for ${formatCurrency(stoppedAmountCents)}`}
                  </button>
                )}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>

    {/* Wrapper lifts the dialog above the timer card (z-[80]) */}
    <div className="relative z-[90]">
    <ConfirmDialog
      open={confirmInvoiceOpen}
      title="Create and send invoice?"
      message={`This creates an invoice for ${formatCurrency(stoppedAmountCents)}${
        stoppedClientName ? ` and emails it to ${stoppedClientName}` : " and emails it to the client"
      }. The time entry will be marked billed.`}
      confirmLabel="Create & Send"
      onConfirm={handleGenerateInvoice}
      onCancel={() => setConfirmInvoiceOpen(false)}
    />
    </div>

    {/* Quick Create Matter Modal */}
    <QuickCreateMatterModal
      open={showQuickCreateMatter}
      onClose={() => setShowQuickCreateMatter(false)}
      onCreated={handleMatterCreated}
    />
    </>
  );
};

export default FloatingTimer;
